import { useEffect, useMemo, useState } from "react";
import type { Bootstrap, AnalysisResult, AnalysisLine } from "../../shared/types";
import { api } from "../lib/api";
import { formatMoney, formatDayMonth, currentPeriod, shiftPeriod } from "../lib/format";
import Segmented from "../components/Segmented";

type Scope = "month" | "year" | "all";

const SYNTH: Record<string, { label: string; colour: string }> = {
  spend: { label: "Day-to-day spends", colour: "#98A2B3" },
};
const SOURCE_LABEL: Record<AnalysisLine["source"], string> = {
  spend: "Spend",
  bill: "Bill",
  plan: "Plan",
};

// Interactive analysis: what the household actually spent (bills ticked paid +
// plan payments + logged spends) over a chosen month / year / all-time, with
// click-to-expand drill-down by category and by type.
export default function Breakdown({ data }: { data: Bootstrap | null }) {
  const [scope, setScope] = useState<Scope>("month");
  const [month, setMonth] = useState(currentPeriod()); // YYYY-MM
  const [year, setYear] = useState(currentPeriod().slice(0, 4)); // YYYY
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [expCat, setExpCat] = useState<string | null>(null);
  const [expType, setExpType] = useState<string | null>(null);
  const [expMember, setExpMember] = useState<number | null>(null);

  const period = scope === "month" ? month : scope === "year" ? year : undefined;

  useEffect(() => {
    let live = true;
    setLoading(true);
    setExpCat(null);
    setExpType(null);
    setExpMember(null);
    api
      .analysis(scope, period)
      .then((r) => live && (setResult(r), setLoading(false)))
      .catch(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [scope, period]);

  const typeMeta = useMemo(() => {
    const m = new Map<string, { label: string; colour: string }>();
    for (const t of data?.types ?? []) m.set(t.key, { label: t.label, colour: t.colour });
    for (const [k, v] of Object.entries(SYNTH)) m.set(k, v);
    return m;
  }, [data]);

  const lines = result?.lines ?? [];
  const total = result?.totalSpentMinor ?? 0;
  const income = result?.incomeMinor ?? null;
  const surplus = income != null ? income - total : null;
  const usePct = income && income > 0 ? Math.round((total / income) * 100) : 0;
  const pctOfTotal = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0);

  const byCategory = useMemo(() => {
    const map = new Map<string, { amount: number; colour: string | null; items: AnalysisLine[] }>();
    for (const l of lines) {
      const name = l.categoryName ?? "Uncategorised";
      const cur = map.get(name) ?? { amount: 0, colour: l.categoryColour, items: [] };
      cur.amount += l.amountMinor;
      cur.items.push(l);
      map.set(name, cur);
    }
    return [...map.entries()].map(([name, v]) => ({ name, ...v })).sort((a, b) => b.amount - a.amount);
  }, [lines]);
  const maxCat = byCategory[0]?.amount ?? 1;

  const byType = useMemo(() => {
    const map = new Map<string, { amount: number; items: AnalysisLine[] }>();
    for (const l of lines) {
      const cur = map.get(l.type) ?? { amount: 0, items: [] };
      cur.amount += l.amountMinor;
      cur.items.push(l);
      map.set(l.type, cur);
    }
    return [...map.entries()].map(([key, v]) => ({ key, ...v })).sort((a, b) => b.amount - a.amount);
  }, [lines]);

  const byMember = useMemo(() => {
    const map = new Map<number, { amount: number; items: AnalysisLine[] }>();
    for (const l of lines) {
      if (l.paidById == null) continue;
      const cur = map.get(l.paidById) ?? { amount: 0, items: [] };
      cur.amount += l.amountMinor;
      cur.items.push(l);
      map.set(l.paidById, cur);
    }
    return map;
  }, [lines]);

  if (!data) return <div className="state">Loading…</div>;

  const step = (dir: number) => {
    if (scope === "month") setMonth(shiftPeriod(month, dir));
    else if (scope === "year") setYear(String(Number(year) + dir));
  };

  return (
    <div className="page breakdown">
      <div className="bd-head">
        <div>
          <h1 className="page__title">Breakdown</h1>
          <p className="page__lede">Where the money actually went — {result?.label ?? "…"}.</p>
        </div>
        <div className="bd-period">
          <Segmented
            value={scope}
            onChange={(v) => setScope(v as Scope)}
            options={[["month", "Month"], ["year", "Year"], ["all", "All time"]]}
          />
          {scope !== "all" && (
            <div className="bd-stepper">
              <button className="month-nav__btn" onClick={() => step(-1)} aria-label="Previous">‹</button>
              <span className="bd-stepper__label">{result?.label ?? "…"}</span>
              <button className="month-nav__btn" onClick={() => step(1)} aria-label="Next">›</button>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="state">Loading…</div>
      ) : total === 0 ? (
        <section className="panel bd-empty">
          <p className="bd-empty__figure">£0.00</p>
          <p>Nothing recorded as spent in {result?.label}.</p>
          <p className="panel__sub">Spending appears here once you tick bills as paid, mark plan payments, or log a spend.</p>
        </section>
      ) : (
        <>
          <section className="panel bd-summary">
            <div className="bd-summary__top">
              {income != null ? (
                <>
                  <div className="bd-flow">
                    <div>
                      <p className="stat-card__label">Income</p>
                      <p className="bd-flow__figure">{formatMoney(income)}</p>
                    </div>
                    <span className="bd-flow__arrow" aria-hidden="true">→</span>
                    <div>
                      <p className="stat-card__label">Spent</p>
                      <p className="bd-flow__figure">{formatMoney(total)}</p>
                    </div>
                  </div>
                  <div className={"bd-surplus " + (surplus! >= 0 ? "is-positive" : "is-negative")}>
                    <p className="bd-surplus__label">{surplus! >= 0 ? "Left over" : "Overspent"}</p>
                    <p className="bd-surplus__figure">{formatMoney(Math.abs(surplus!))}</p>
                    <p className="bd-surplus__sub">{surplus! >= 0 ? "income covered spending" : "spending exceeded income"}</p>
                  </div>
                </>
              ) : (
                <div className="bd-flow">
                  <div>
                    <p className="stat-card__label">Total spent</p>
                    <p className="bd-flow__figure">{formatMoney(total)}</p>
                  </div>
                </div>
              )}
            </div>
            {income != null && (
              <>
                <div className="meter bd-summary__meter">
                  <span style={{ width: `${Math.min(usePct, 100)}%`, background: usePct > 100 ? "var(--color-status-red)" : "var(--color-primary)" }} />
                </div>
                <p className="bd-summary__note">Spending used {usePct}% of income</p>
              </>
            )}
          </section>

          {byMember.size > 0 && (
            <section className="person-grid">
              {data.household.members.map((m) => {
                const entry = byMember.get(m.id);
                const spent = entry?.amount ?? 0;
                const items = entry?.items ?? [];
                const isOpen = expMember === m.id;
                return (
                  <div className={"person-card" + (isOpen ? " is-open" : "")} key={m.id}>
                    <button
                      className="person-card__toggle"
                      onClick={() => items.length && setExpMember(isOpen ? null : m.id)}
                      disabled={!items.length}
                    >
                      <div className="person-card__head">
                        <span className="avatar avatar--lg" style={{ background: m.colour }}>{m.initial}</span>
                        <div>
                          <p className="person-card__name">{m.name}</p>
                          <p className="person-card__sub">{pctOfTotal(spent)}% of spending</p>
                        </div>
                        {items.length > 0 && (
                          <span className={"drill-row__chevron" + (isOpen ? " is-open" : "")} aria-hidden="true">›</span>
                        )}
                      </div>
                      <div className="person-card__row"><span>Spent</span><span>{formatMoney(spent)}</span></div>
                      <div className="meter"><span style={{ width: `${Math.min(pctOfTotal(spent), 100)}%`, background: m.colour }} /></div>
                    </button>
                    {isOpen && <GroupedLineItems items={items} />}
                  </div>
                );
              })}
            </section>
          )}

          <div className="two-col">
            <section className="panel">
              <h3 className="panel__title">By category</h3>
              <p className="panel__sub">Tap a category to see what made it up</p>
              {byCategory.map((c) => (
                <div key={c.name}>
                  <button className="drill-row" onClick={() => setExpCat(expCat === c.name ? null : c.name)}>
                    <span className="cat-name"><span className="chip__dot" style={{ background: c.colour ?? "#98A2B3" }} />{c.name}</span>
                    <span className="drill-row__right">
                      <span className="meter-row__val">{formatMoney(c.amount)} <em>{pctOfTotal(c.amount)}%</em></span>
                      <span className={"drill-row__chevron" + (expCat === c.name ? " is-open" : "")} aria-hidden="true">›</span>
                    </span>
                  </button>
                  <div className="meter"><span style={{ width: `${Math.round((c.amount / maxCat) * 100)}%`, background: c.colour ?? "#98A2B3" }} /></div>
                  {expCat === c.name && <GroupedLineItems items={c.items} />}
                </div>
              ))}
            </section>

            <section className="panel">
              <h3 className="panel__title">By type</h3>
              <p className="panel__sub">Tap a type to see what made it up</p>
              {byType.map((t) => {
                const meta = typeMeta.get(t.key) ?? { label: t.key, colour: "#98A2B3" };
                return (
                  <div key={t.key}>
                    <button className="drill-row" onClick={() => setExpType(expType === t.key ? null : t.key)}>
                      <span className="type-row__badge" style={{ background: meta.colour + "22", color: meta.colour }}>{pctOfTotal(t.amount)}%</span>
                      <span className="drill-row__main">{meta.label}</span>
                      <span className="drill-row__right">
                        <span className="meter-row__val">{formatMoney(t.amount)}</span>
                        <span className={"drill-row__chevron" + (expType === t.key ? " is-open" : "")} aria-hidden="true">›</span>
                      </span>
                    </button>
                    {expType === t.key && <GroupedLineItems items={t.items} />}
                  </div>
                );
              })}
            </section>
          </div>
        </>
      )}
    </div>
  );
}

// Drill-down that first groups payments by who they went to (the label, e.g.
// "Next", "Netflix") and shows each one's TOTAL. A merchant with more than one
// payment expands again into the individual payments. Over a year or all-time
// this turns a long flat list into a scannable "£X to Next, £Y to Netflix…".
function GroupedLineItems({ items }: { items: AnalysisLine[] }) {
  const [open, setOpen] = useState<string | null>(null);

  const groups = useMemo(() => {
    const map = new Map<string, { label: string; total: number; items: AnalysisLine[] }>();
    for (const l of items) {
      const cur = map.get(l.label) ?? { label: l.label, total: 0, items: [] };
      cur.total += l.amountMinor;
      cur.items.push(l);
      map.set(l.label, cur);
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [items]);

  return (
    <div className="drill-items">
      {groups.map((g) => {
        const multi = g.items.length > 1;
        const isOpen = open === g.label;
        return (
          <div className="drill-group" key={g.label}>
            <button
              className="drill-item drill-item--group"
              onClick={() => multi && setOpen(isOpen ? null : g.label)}
              disabled={!multi}
            >
              <span className="drill-item__label">
                {g.label}
                {multi ? (
                  <span className="drill-item__count">{g.items.length} payments</span>
                ) : (
                  <span className={"drill-item__tag drill-item__tag--" + g.items[0].source}>{SOURCE_LABEL[g.items[0].source]}</span>
                )}
              </span>
              <span className="drill-item__amount">
                {formatMoney(g.total)}
                {multi && <span className={"drill-row__chevron" + (isOpen ? " is-open" : "")} aria-hidden="true">›</span>}
              </span>
            </button>
            {multi && isOpen && (
              <div className="drill-subitems">
                {[...g.items].sort((a, b) => (a.date < b.date ? 1 : -1)).map((l, i) => (
                  <div className="drill-item drill-item--sub" key={i}>
                    <span className="drill-item__date">{formatDayMonth(l.date)}</span>
                    <span className="drill-item__label">
                      <span className={"drill-item__tag drill-item__tag--" + l.source}>{SOURCE_LABEL[l.source]}</span>
                    </span>
                    <span className="drill-item__amount">{formatMoney(l.amountMinor)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
